import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  fetchExtractorVersions,
  fetchLegacyExtractor,
  fetchExtractor,
  fetchExtractorVersion,
  fetchOrgExtractors,
  fetchExtractorSourcesForExtractor,
  fetchExtractorVersionsForExtractor,
} from '_/api/extractors/extractors.api';
import { IExtendedExtractorVersion, IExtractorVersion } from '_/types/extractors/extractors.types';

export const EXTRACTOR_VERSIONS_QUERY_KEY = (extractorId: string): (string | number)[] => ['extractor', extractorId, 'versions'];

export const LEGACY_EXTRACTOR_QUERY_KEY = (extractorId: string): (string | number)[] => ['legacyExtractor', extractorId];

export const EXTRACTOR_QUERY_KEY = (extractorId: string): (string | number)[] => ['extractor', extractorId];

export const EXTRACTOR_VERSION_QUERY_KEY = (
  extractorVersionId: string,
  extractorId?: string,
  allVersions?: any[],
): (string | undefined | any[])[] => ['extractorVersion', extractorVersionId, extractorId, allVersions];

export const useExtractorVersions = (extractorId: string | undefined): UseQueryResult<IExtractorVersion[]> => {
  return useQuery({
    queryKey: EXTRACTOR_VERSIONS_QUERY_KEY(extractorId || ''),
    queryFn: () => fetchExtractorVersions(extractorId!),
    enabled: !!extractorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useLegacyExtractor = (extractorId: string | undefined): UseQueryResult<any> => {
  return useQuery({
    queryKey: LEGACY_EXTRACTOR_QUERY_KEY(extractorId || ''),
    queryFn: () => fetchLegacyExtractor(extractorId!),
    enabled: !!extractorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useExtractor = (extractorId: string | undefined): UseQueryResult<any> => {
  return useQuery({
    queryKey: EXTRACTOR_QUERY_KEY(extractorId || ''),
    queryFn: () => fetchExtractor(extractorId!),
    enabled: !!extractorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useExtractorVersion = (
  extractorVersionId: string | undefined,
  extractorId: string | undefined,
): UseQueryResult<IExtendedExtractorVersion | null> => {
  // Get all versions first to calculate previousTag
  const { data: allVersions = [] } = useExtractorVersions(extractorId);

  return useQuery({
    queryKey: EXTRACTOR_VERSION_QUERY_KEY(extractorVersionId || '', extractorId, allVersions),
    queryFn: () => fetchExtractorVersion(extractorVersionId!, allVersions),
    enabled: !!extractorVersionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Query keys for organization-level extractors
export const ORG_EXTRACTORS_QUERY_KEY = (orgId: string): string[] => ['org', orgId, 'extractors'];
export const EXTRACTOR_SOURCES_QUERY_KEY = (extractorId: string): string[] => ['extractor', extractorId, 'sources'];
export const EXTRACTOR_VERSIONS_FOR_EXTRACTOR_QUERY_KEY = (extractorId: string): string[] => [
  'extractor',
  extractorId,
  'versionsForExtractor',
];

/**
 * Hook to fetch organization extractors with pagination
 */
export const useOrgExtractors = (orgId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ORG_EXTRACTORS_QUERY_KEY(orgId || ''),
    queryFn: async () => {
      const response = await fetchOrgExtractors(orgId!);
      return response;
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch sources for a specific extractor with pagination
 */
export const useExtractorSources = (extractorId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: EXTRACTOR_SOURCES_QUERY_KEY(extractorId || ''),
    queryFn: async () => {
      const response = await fetchExtractorSourcesForExtractor(extractorId!);
      return response;
    },
    enabled: !!extractorId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch versions for a specific extractor with pagination
 */
export const useExtractorVersionsForExtractor = (extractorId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: EXTRACTOR_VERSIONS_FOR_EXTRACTOR_QUERY_KEY(extractorId || ''),
    queryFn: async () => {
      const response = await fetchExtractorVersionsForExtractor(extractorId!);
      return response;
    },
    enabled: !!extractorId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to load more organization extractors
 */
export const useFetchMoreOrgExtractors = (orgId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      const response = await fetchOrgExtractors(orgId!, cursor);
      return response;
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(ORG_EXTRACTORS_QUERY_KEY(orgId!), (oldData: any) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to load more extractor sources
 */
export const useFetchMoreExtractorSources = (extractorId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      const response = await fetchExtractorSourcesForExtractor(extractorId!, cursor);
      return response;
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(EXTRACTOR_SOURCES_QUERY_KEY(extractorId!), (oldData: any) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to load more extractor versions
 */
export const useFetchMoreExtractorVersions = (extractorId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cursor: string) => {
      const response = await fetchExtractorVersionsForExtractor(extractorId!, cursor);
      return response;
    },
    onSuccess: (newData) => {
      queryClient.setQueryData(EXTRACTOR_VERSIONS_FOR_EXTRACTOR_QUERY_KEY(extractorId!), (oldData: any) => {
        if (!oldData) return newData;
        return {
          items: [...oldData.items, ...newData.items],
          cursor: newData.cursor,
        };
      });
    },
  });
};

/**
 * Hook to fetch assets (sources and versions) for an extractor
 */
export const useFetchExtractorAssets = (): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Trigger fetches for both sources and versions
      await Promise.allSettled([
        queryClient.fetchQuery({
          queryKey: EXTRACTOR_SOURCES_QUERY_KEY(id),
          queryFn: () => fetchExtractorSourcesForExtractor(id),
        }),
        queryClient.fetchQuery({
          queryKey: EXTRACTOR_VERSIONS_FOR_EXTRACTOR_QUERY_KEY(id),
          queryFn: () => fetchExtractorVersionsForExtractor(id),
        }),
      ]);
    },
  });
};
